// src/components/settings/shared/dialogs/KennelDialog.tsx
import React, { useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { Edit as EditIcon, Add as AddIcon } from '@mui/icons-material';
import DialogBase from '../../../common/modal/DialogBase';
import {
  DailogLabel,
  DialogLabelText,
  StyledTextField,
  StyledSwitch,
} from '../../../forms/shared/FormElements';
import FieldHelperText from '../../../forms/shared/FieldHelperText';
import { ModalCancelButton, ModalSaveButton } from '../../../common/Buttons';
import CustomSelect from '../../../common/CustomSelect';
import CustomSelectMenuItem from '../../../common/CustomSelectMenuItem';
import { Kennel, KennelSize } from '../../../../services/api';

interface KennelFormData {
  kennelNumber: number;
  kennelName: string;
  kennelSizeId: number;
  kennelSideId: number;
  active?: boolean;
}

interface KennelDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: KennelFormData) => void;
  editing: Kennel | null;
  sideId: number;
  kennelSizes: KennelSize[];
}

const KennelDialog: React.FC<KennelDialogProps> = ({
  open,
  onClose,
  onSave,
  editing,
  sideId,
  kennelSizes,
}) => {
  const [formData, setFormData] = useState<KennelFormData>({
    kennelNumber: 0,
    kennelName: '',
    kennelSizeId: 0,
    kennelSideId: 0,
    active: true,
  });

  useEffect(() => {
    if (open) {
      const defaultSizeId = kennelSizes.length > 0 ? kennelSizes[0].id : 0;
      setFormData(
        editing
          ? {
              kennelNumber: editing.kennelNumber,
              kennelName: editing.kennelName || '',
              kennelSizeId: editing.kennelSizeId ?? defaultSizeId,
              kennelSideId: sideId,
              active: editing.active ?? true,
            }
          : {
              kennelNumber: 0,
              kennelName: '',
              kennelSizeId: defaultSizeId,
              kennelSideId: sideId,
              active: true,
            }
      );
    }
  }, [open, editing, sideId, kennelSizes]);

  const canSave = formData.kennelNumber > 0 && formData.kennelSizeId > 0;
  const isSystem = !!editing?.isSystem;

  const handleSave = () => {
    if (editing) {
      onSave(formData);
    } else {
      const { active, ...createPayload } = formData;
      onSave(createPayload);
    }
  };

  return (
    <DialogBase
      open={open}
      onClose={onClose}
      title={editing ? 'Edit Kennel' : 'Add Kennel'}
      headerIcon={editing ? <EditIcon /> : <AddIcon />}
      draggable
      footer={
        <>
          <ModalCancelButton
            onClick={onClose}
            sx={{
              minWidth: { xs: 'auto', sm: '160px' },
              '& .MuiButton-startIcon': { display: { xs: 'none', sm: 'flex' } },
            }}
          />
          <ModalSaveButton
            onClick={handleSave}
            disabled={!canSave}
            text="Save"
            sx={{
              minWidth: { xs: 'auto', sm: '160px' },
              '& .MuiButton-startIcon': { display: { xs: 'none', sm: 'flex' } },
            }}
          />
        </>
      }
    >
      <Box display="flex" flexDirection="column" gap={0.5} mt={0.2}>
        <DailogLabel>
          <DialogLabelText>Kennel Number:</DialogLabelText>
        </DailogLabel>
        <StyledTextField
          type="number"
          value={formData.kennelNumber}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setFormData({ ...formData, kennelNumber: Number(e.target.value) })
          }
          required
          fullWidth
          disabled={isSystem}
        />
        {isSystem && (
          <FieldHelperText helperText="Standard system item - number and size cannot be changed. You can still deactivate it below." />
        )}
        <DailogLabel>
          <DialogLabelText>Kennel Name (optional):</DialogLabelText>
        </DailogLabel>
        <StyledTextField
          value={formData.kennelName}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setFormData({ ...formData, kennelName: e.target.value })
          }
          fullWidth
          placeholder="e.g. Sunny Suite"
        />
        <DailogLabel>
          <DialogLabelText>Size:</DialogLabelText>
        </DailogLabel>
        <CustomSelect
          value={String(formData.kennelSizeId)}
          onChange={(value) =>
            setFormData({ ...formData, kennelSizeId: Number(value) })
          }
          ariaLabel="Size"
          required
          disabled={isSystem}
        >
          {kennelSizes.map((ks) => (
            <CustomSelectMenuItem key={ks.id} value={String(ks.id)}>
              {ks.name}
            </CustomSelectMenuItem>
          ))}
        </CustomSelect>
        {editing && (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            mt={1}
          >
            <Typography sx={{ fontSize: '1.8rem', fontWeight: 600 }}>
              Active
            </Typography>
            <StyledSwitch
              checked={formData.active}
              onChange={(e) =>
                setFormData({ ...formData, active: e.target.checked })
              }
            />
          </Box>
        )}
      </Box>
    </DialogBase>
  );
};

export default KennelDialog;
